import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  ChevronRight,
  Download,
  LockKeyhole,
  Mic,
  RefreshCcw,
  ShieldCheck,
} from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import {
  buildDemoMatterPath,
  CORPORATE_MA_DEMO,
  DEFAULT_DEMO_ROUTE,
  DEFAULT_DEMO_SCOPE_ID,
  DEMO_ACCESS_STORAGE_KEY,
  DEMO_SCOPE_OPTIONS,
  DEMO_WORKSPACE_SECTIONS,
  getDemoHomeState,
  getDemoRouteState,
  isMatterVisibleInScope,
  isValidDemoAccessCode,
  normalizeDemoPath,
} from '@/lib/corporateMaDemo';
import './DemoPage.css';

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function readDemoUnlockState() {
  if (!canUseSessionStorage()) return false;
  return window.sessionStorage.getItem(DEMO_ACCESS_STORAGE_KEY) === '1';
}

function writeDemoUnlockState(unlocked) {
  if (!canUseSessionStorage()) return;
  if (unlocked) {
    window.sessionStorage.setItem(DEMO_ACCESS_STORAGE_KEY, '1');
    return;
  }
  window.sessionStorage.removeItem(DEMO_ACCESS_STORAGE_KEY);
}

function buildSelectionMap(getDefaultId) {
  return Object.fromEntries(
    CORPORATE_MA_DEMO.matters.map((matter) => [matter.id, getDefaultId(matter)]),
  );
}

function buildInitialUiState() {
  return {
    selectedScopeId: DEFAULT_DEMO_SCOPE_ID,
    selectedFileIdByMatter: buildSelectionMap((matter) => matter.workspace.files.items[0]?.id || null),
    selectedCaptureIdByMatter: buildSelectionMap((matter) => matter.workspace.jolly.captures[0]?.id || null),
    selectedReviewIdByMatter: buildSelectionMap((matter) => matter.workspace.review.findings[0]?.id || null),
  };
}

function getSelectedItem(items, selectedId) {
  return items.find((item) => item.id === selectedId) || items[0];
}

function getToneStyle(value) {
  const normalized = String(value || '').toLowerCase();

  if (['blocked', 'high', 'action needed', 'needs owner'].includes(normalized)) {
    return {
      color: '#8c2f28',
      background: 'rgba(185, 71, 59, 0.10)',
      border: 'rgba(185, 71, 59, 0.18)',
    };
  }

  if (['watch', 'queued', 'in draft', 'in progress', 'medium'].includes(normalized)) {
    return {
      color: '#8a6425',
      background: 'rgba(173, 129, 48, 0.10)',
      border: 'rgba(173, 129, 48, 0.18)',
    };
  }

  return {
    color: '#295646',
    background: 'rgba(41, 86, 70, 0.10)',
    border: 'rgba(41, 86, 70, 0.18)',
  };
}

function TonePill({ children, value, className = '' }) {
  const tone = getToneStyle(value);

  return (
    <span
      className={`demo-pill ${className}`}
      style={{
        color: tone.color,
        background: tone.background,
        borderColor: tone.border,
      }}
    >
      {children}
    </span>
  );
}

function DemoPanel({ label, title, action, className = '', bodyClassName = '', children }) {
  return (
    <section className={`demo-panel ${className}`}>
      {(label || title || action) ? (
        <div className="demo-panel__header">
          <div className="demo-panel__copy">
            {label ? <span className="demo-panel__label">{label}</span> : null}
            {title ? <h2 className="demo-panel__title">{title}</h2> : null}
          </div>
          {action ? <div className="demo-panel__action">{action}</div> : null}
        </div>
      ) : null}
      <div className={`demo-panel__body ${bodyClassName}`}>{children}</div>
    </section>
  );
}

function ScopeSwitcher({ selectedScopeId, onChange }) {
  return (
    <div className="demo-scope-switcher" role="tablist" aria-label="Demo scope">
      {DEMO_SCOPE_OPTIONS.map((scope) => (
        <button
          key={scope.id}
          type="button"
          className={`demo-scope-switcher__button ${scope.id === selectedScopeId ? 'is-active' : ''}`}
          onClick={() => onChange(scope.id)}
        >
          {scope.shortLabel}
        </button>
      ))}
    </div>
  );
}

function AccessGate({ code, error, onChange, onSubmit }) {
  return (
    <div className="demo-gate-shell">
      <div className="demo-gate-grid">
        <section className="demo-gate-card demo-gate-card--intro">
          <div className="demo-brand">
            <span className="demo-brand__mark">
              <BrandLogo variant="mark" className="demo-brand__mark-image" />
            </span>
            <div>
              <p className="demo-topbar__eyebrow">{CORPORATE_MA_DEMO.meta.title}</p>
              <strong className="demo-topbar__title">{CORPORATE_MA_DEMO.meta.projectName}</strong>
            </div>
          </div>

          <h1 className="demo-gate-card__title">Open demo</h1>
          <p className="demo-gate-card__summary">{CORPORATE_MA_DEMO.meta.entrySummary}</p>

          <div className="demo-gate-facts">
            <div className="demo-gate-fact">
              <span className="demo-panel__label">Matter</span>
              <strong>{CORPORATE_MA_DEMO.meta.projectName}</strong>
            </div>
            <div className="demo-gate-fact">
              <span className="demo-panel__label">Workspace</span>
              <strong>{CORPORATE_MA_DEMO.meta.workspaceLabel}</strong>
            </div>
            <div className="demo-gate-fact">
              <span className="demo-panel__label">Flow</span>
              <strong>Matters to export</strong>
            </div>
          </div>
        </section>

        <section className="demo-gate-card">
          <div className="demo-panel__copy">
            <span className="demo-panel__label">Protected demo</span>
            <h2 className="demo-panel__title">Enter access code</h2>
          </div>

          <form className="demo-gate-form" onSubmit={onSubmit}>
            <label className="demo-gate-field">
              <span className="demo-gate-field__label">Code</span>
              <div className="demo-gate-field__input">
                <LockKeyhole size={16} />
                <input
                  type="password"
                  value={code}
                  onChange={(event) => onChange(event.target.value)}
                  placeholder="Enter demo access code"
                  autoComplete="off"
                />
              </div>
            </label>

            {error ? <p className="demo-gate-field__error">{error}</p> : null}

            <button type="submit" className="demo-button demo-button--primary">
              Open demo
              <ArrowRight size={15} />
            </button>
          </form>

          <div className="demo-gate-note">
            <ShieldCheck size={16} />
            <span>State stays resettable for live walkthroughs.</span>
          </div>
        </section>
      </div>
    </div>
  );
}

function HomeView({ scopeId, onOpenMatter }) {
  const homeState = getDemoHomeState(scopeId);
  const showOwner = scopeId === 'team';

  return (
    <div className="demo-view">
      <div className="demo-view__header">
        <div className="demo-view__copy">
          <h1 className="demo-view__title">Matters</h1>
          <p className="demo-view__subtitle">{homeState.scope.label}</p>
        </div>
      </div>

      <div className="demo-home-grid">
        <DemoPanel
          label="Resume"
          title={homeState.resumeMatter.name}
          action={(
            <button
              type="button"
              className="demo-button demo-button--primary"
              onClick={() => onOpenMatter(homeState.resumeMatter.id)}
            >
              {homeState.resumeMatter.resumeLabel}
              <ArrowRight size={15} />
            </button>
          )}
          className="demo-panel--scroll"
        >
          <div className="demo-resume-card">
            <div className="demo-resume-card__top">
              <TonePill value={homeState.resumeMatter.status}>{homeState.resumeMatter.stage}</TonePill>
              <span className="demo-meta-chip">{homeState.resumeMatter.nextDateLabel}: {homeState.resumeMatter.nextDate}</span>
            </div>
            <strong className="demo-resume-card__title">{homeState.resumeMatter.summary}</strong>
            <div className="demo-key-grid">
              <div>
                <span>Client</span>
                <strong>{homeState.resumeMatter.client}</strong>
              </div>
              <div>
                <span>Target</span>
                <strong>{homeState.resumeMatter.counterparty}</strong>
              </div>
              <div>
                <span>Files</span>
                <strong>{homeState.resumeMatter.fileCount}</strong>
              </div>
              <div>
                <span>Last touch</span>
                <strong>{homeState.resumeMatter.lastTouch}</strong>
              </div>
            </div>
          </div>

          <div className="demo-subsection">
            <span className="demo-subsection__title">Recent work</span>
            <div className="demo-list">
              {homeState.recentWork.map((entry) => (
                <div key={`${entry.matterId}-${entry.title}`} className="demo-list-row">
                  <div className="demo-list-row__copy">
                    <strong>{entry.title}</strong>
                    <p>{entry.matter.name} · {entry.detail}</p>
                  </div>
                  <div className="demo-list-row__meta">
                    {entry.owner ? <span>{entry.owner}</span> : null}
                    <span>{entry.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DemoPanel>

        <DemoPanel label="Active matters" title={`${homeState.matters.length} open`} className="demo-panel--scroll">
          <div className="demo-list">
            {homeState.matters.map((matter) => (
              <button
                key={matter.id}
                type="button"
                className="demo-matter-row"
                onClick={() => onOpenMatter(matter.id)}
              >
                <div className="demo-matter-row__copy">
                  <div className="demo-matter-row__title">
                    <strong>{matter.name}</strong>
                    <TonePill value={matter.status}>{matter.status}</TonePill>
                  </div>
                  <p>{matter.client} · {matter.counterparty}</p>
                  <div className="demo-matter-row__meta">
                    <span>{matter.matterType}</span>
                    <span>{matter.nextDateLabel}: {matter.nextDate}</span>
                    <span>{matter.lastTouch}</span>
                    {showOwner ? <span>{matter.owner}</span> : null}
                  </div>
                </div>
                <ChevronRight size={17} className="demo-matter-row__chevron" />
              </button>
            ))}
          </div>
        </DemoPanel>

        <div className="demo-home-side">
          <DemoPanel label="Drafts" title="Drafts in progress" className="demo-panel--scroll">
            <div className="demo-list">
              {homeState.draftsInProgress.map((entry) => (
                <div key={`${entry.matterId}-${entry.title}`} className="demo-list-row">
                  <div className="demo-list-row__copy">
                    <strong>{entry.title}</strong>
                    <p>{entry.matter.name}</p>
                  </div>
                  <div className="demo-list-row__meta">
                    <TonePill value={entry.status}>{entry.status}</TonePill>
                    <span>{entry.updated}</span>
                  </div>
                </div>
              ))}
            </div>
          </DemoPanel>

          <DemoPanel label="Exports" title="Recent exports" className="demo-panel--scroll">
            <div className="demo-list">
              {homeState.recentExports.map((entry) => (
                <div key={`${entry.matterId}-${entry.title}`} className="demo-list-row">
                  <div className="demo-list-row__copy">
                    <strong>{entry.title}</strong>
                    <p>{entry.matter.name}</p>
                  </div>
                  <div className="demo-list-row__meta">
                    <TonePill value="ready">{entry.format}</TonePill>
                    <span>{entry.updated}</span>
                  </div>
                </div>
              ))}
            </div>
          </DemoPanel>
        </div>
      </div>
    </div>
  );
}

function MatterResumePanel({ matter }) {
  return (
    <DemoPanel label="Resume" title={matter.stage} className="demo-panel--scroll">
      <div className="demo-subsection">
        <span className="demo-subsection__title">Current position</span>
        <p className="demo-copy-block">{matter.workspace.resume.currentPosition}</p>
      </div>

      <div className="demo-subsection">
        <span className="demo-subsection__title">Next steps</span>
        <div className="demo-detail-list">
          {matter.workspace.resume.nextSteps.map((item) => (
            <div key={item} className="demo-detail-row">
              <span className="demo-dot" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="demo-subsection">
        <span className="demo-subsection__title">Key facts</span>
        <div className="demo-key-grid">
          {matter.workspace.resume.keyFacts.map((fact) => (
            <div key={fact.label}>
              <span>{fact.label}</span>
              <strong>{fact.value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="demo-subsection">
        <span className="demo-subsection__title">Latest activity</span>
        <div className="demo-detail-list">
          {matter.workspace.resume.latestActivity.map((item) => (
            <div key={item} className="demo-detail-row">
              <span className="demo-dot" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </DemoPanel>
  );
}

function OverviewSection({ matter }) {
  return (
    <div className="demo-section-grid">
      <div className="demo-note-card">
        <span className="demo-subsection__title">Summary</span>
        <p className="demo-copy-block">{matter.workspace.overview.summary}</p>
      </div>

      <div className="demo-note-card">
        <span className="demo-subsection__title">Watch list</span>
        <div className="demo-list">
          {matter.workspace.overview.watchItems.map((item) => (
            <div key={item.title} className="demo-list-row demo-list-row--compact">
              <div className="demo-list-row__copy">
                <strong>{item.title}</strong>
                <p>{item.detail}</p>
              </div>
              <div className="demo-list-row__meta">
                <TonePill value={item.tone}>{item.tone}</TonePill>
                <span>{item.meta}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="demo-note-card">
        <div className="demo-note-card__top">
          <span className="demo-subsection__title">{matter.workspace.overview.synthesis.label}</span>
          <span className="demo-caption">{matter.workspace.overview.synthesis.caption}</span>
        </div>
        <div className="demo-structured-columns">
          {matter.workspace.overview.synthesis.sections.map((section) => (
            <div key={section.title} className="demo-structured-column">
              <strong>{section.title}</strong>
              <div className="demo-detail-list">
                {section.items.map((item) => (
                  <div key={item} className="demo-detail-row">
                    <span className="demo-dot" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="demo-note-card">
        <span className="demo-subsection__title">Timeline</span>
        <div className="demo-list">
          {matter.workspace.overview.timeline.map((item) => (
            <div key={`${item.date}-${item.label}`} className="demo-list-row demo-list-row--compact">
              <div className="demo-list-row__copy">
                <strong>{item.label}</strong>
                <p>{item.detail}</p>
              </div>
              <div className="demo-list-row__meta">
                <span>{item.date}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DraftSection({ matter }) {
  return (
    <div className="demo-section-grid">
      <div className="demo-note-card">
        <div className="demo-note-card__top">
          <span className="demo-subsection__title">{matter.workspace.draft.current.title}</span>
          <div className="demo-inline-meta">
            <TonePill value={matter.workspace.draft.current.status}>{matter.workspace.draft.current.status}</TonePill>
            <span>{matter.workspace.draft.current.updated}</span>
          </div>
        </div>

        <div className="demo-structured-columns">
          {matter.workspace.draft.sections.map((section) => (
            <div key={section.title} className="demo-structured-column">
              <strong>{section.title}</strong>
              {section.text ? <p className="demo-copy-block">{section.text}</p> : null}
              {section.items ? (
                <div className="demo-detail-list">
                  {section.items.map((item) => (
                    <div key={item} className="demo-detail-row">
                      <span className="demo-dot" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="demo-note-card">
        <span className="demo-subsection__title">Negotiation positions</span>
        <div className="demo-list">
          {matter.workspace.draft.negotiations.map((item) => (
            <div key={item.clause} className="demo-list-row demo-list-row--stacked">
              <div className="demo-list-row__copy">
                <strong>{item.clause}</strong>
                <p>{item.lead}</p>
              </div>
              <div className="demo-list-row__meta demo-list-row__meta--start">
                <span>{item.fallback}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FilesSection({ matter, selectedFileId, onSelectFile }) {
  const selectedFile = getSelectedItem(matter.workspace.files.items, selectedFileId);

  return (
    <div className="demo-split-grid">
      <div className="demo-note-card">
        <span className="demo-subsection__title">Files</span>
        <div className="demo-folder-strip">
          {matter.workspace.files.folders.map((folder) => (
            <div key={folder.name} className="demo-folder-pill">
              <strong>{folder.name}</strong>
              <span>{folder.count}</span>
              <small>{folder.note}</small>
            </div>
          ))}
        </div>

        <div className="demo-list">
          {matter.workspace.files.items.map((file) => (
            <button
              key={file.id}
              type="button"
              className={`demo-select-row ${file.id === selectedFile.id ? 'is-active' : ''}`}
              onClick={() => onSelectFile(file.id)}
            >
              <div className="demo-list-row__copy">
                <strong>{file.name}</strong>
                <p>{file.label} · {file.summary}</p>
              </div>
              <div className="demo-list-row__meta">
                <span>{file.meta}</span>
                <span>{file.updated}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="demo-note-card">
        <div className="demo-note-card__top">
          <span className="demo-subsection__title">{selectedFile.name}</span>
          <span className="demo-caption">{selectedFile.label}</span>
        </div>
        <p className="demo-copy-block">{selectedFile.summary}</p>

        <div className="demo-structured-columns">
          <div className="demo-structured-column">
            <strong>Key facts</strong>
            <div className="demo-detail-list">
              {selectedFile.highlights.map((item) => (
                <div key={item} className="demo-detail-row">
                  <span className="demo-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="demo-structured-column">
            <strong>Missing</strong>
            <div className="demo-detail-list">
              {selectedFile.missing.map((item) => (
                <div key={item} className="demo-detail-row">
                  <span className="demo-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function JollySection({ matter, selectedCaptureId, onSelectCapture }) {
  const selectedCapture = getSelectedItem(matter.workspace.jolly.captures, selectedCaptureId);

  return (
    <div className="demo-split-grid">
      <div className="demo-note-card">
        <span className="demo-subsection__title">Captures</span>
        <div className="demo-list">
          {matter.workspace.jolly.captures.map((capture) => (
            <button
              key={capture.id}
              type="button"
              className={`demo-select-row ${capture.id === selectedCapture.id ? 'is-active' : ''}`}
              onClick={() => onSelectCapture(capture.id)}
            >
              <div className="demo-list-row__copy">
                <strong>{capture.title}</strong>
                <p>{capture.summary}</p>
              </div>
              <div className="demo-list-row__meta">
                <span>{capture.time}</span>
                <span>{capture.duration}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="demo-note-card">
        <div className="demo-note-card__top">
          <span className="demo-subsection__title">{selectedCapture.title}</span>
          <span className="demo-caption">Captured with Jolly</span>
        </div>
        <p className="demo-copy-block">{selectedCapture.summary}</p>
        <div className="demo-structured-columns">
          {selectedCapture.noteSections.map((section) => (
            <div key={section.title} className="demo-structured-column">
              <strong>{section.title}</strong>
              <div className="demo-detail-list">
                {section.items.map((item) => (
                  <div key={item} className="demo-detail-row">
                    <span className="demo-dot" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewSection({ matter, selectedReviewId, onSelectReview }) {
  const selectedFinding = getSelectedItem(matter.workspace.review.findings, selectedReviewId);

  return (
    <div className="demo-split-grid">
      <div className="demo-note-card">
        <div className="demo-note-card__top">
          <span className="demo-subsection__title">Review</span>
          <span className="demo-caption">{matter.workspace.review.caption}</span>
        </div>
        <div className="demo-list">
          {matter.workspace.review.findings.map((finding) => (
            <button
              key={finding.id}
              type="button"
              className={`demo-select-row ${finding.id === selectedFinding.id ? 'is-active' : ''}`}
              onClick={() => onSelectReview(finding.id)}
            >
              <div className="demo-list-row__copy">
                <strong>{finding.title}</strong>
                <p>{finding.summary}</p>
              </div>
              <div className="demo-list-row__meta">
                <TonePill value={finding.severity}>{finding.severity}</TonePill>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="demo-note-card">
        <div className="demo-note-card__top">
          <span className="demo-subsection__title">{selectedFinding.title}</span>
          <TonePill value={selectedFinding.severity}>{selectedFinding.severity}</TonePill>
        </div>
        <p className="demo-copy-block">{selectedFinding.summary}</p>

        <div className="demo-structured-columns">
          <div className="demo-structured-column">
            <strong>Risks / gaps</strong>
            <div className="demo-detail-list">
              {selectedFinding.risks.map((item) => (
                <div key={item} className="demo-detail-row">
                  <span className="demo-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="demo-structured-column">
            <strong>Suggested fixes</strong>
            <div className="demo-detail-list">
              {selectedFinding.fixes.map((item) => (
                <div key={item} className="demo-detail-row">
                  <span className="demo-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="demo-subsection">
          <span className="demo-subsection__title">References</span>
          <div className="demo-inline-meta">
            {selectedFinding.references.map((item) => (
              <TonePill key={item} value="watch">{item}</TonePill>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function renderMatterSection(sectionId, props) {
  switch (sectionId) {
    case 'draft':
      return <DraftSection {...props} />;
    case 'files':
      return <FilesSection {...props} />;
    case 'jolly':
      return <JollySection {...props} />;
    case 'review':
      return <ReviewSection {...props} />;
    case 'overview':
    default:
      return <OverviewSection {...props} />;
  }
}

function OutputsPanel({ matter }) {
  return (
    <DemoPanel
      label="Outputs"
      title="Ready to send"
      className="demo-panel--scroll"
      action={(
        <button type="button" className="demo-button demo-button--primary">
          <Download size={15} />
          Export
        </button>
      )}
    >
      <div className="demo-output-stack">
        {matter.workspace.outputs.map((output) => (
          <div key={output.title} className="demo-output-card">
            <div className="demo-output-card__top">
              <div>
                <span className="demo-output-card__label">{output.label}</span>
                <strong>{output.title}</strong>
              </div>
              <TonePill value={output.status}>{output.status}</TonePill>
            </div>
            <p>{output.summary}</p>
            <div className="demo-output-card__bottom">
              <span>{output.updated}</span>
              <button type="button" className="demo-button demo-button--ghost">
                {output.actionLabel}
              </button>
            </div>
          </div>
        ))}
      </div>
    </DemoPanel>
  );
}

function MatterView({
  matter,
  sectionId,
  scopeId,
  onBack,
  onOpenSection,
  uiState,
  setUiState,
}) {
  return (
    <div className="demo-view">
      <div className="demo-view__header demo-view__header--workspace">
        <div className="demo-view__copy demo-view__copy--workspace">
          <button type="button" className="demo-icon-button" onClick={onBack} aria-label="Back to matters">
            <ArrowLeft size={16} />
          </button>

          <div>
            <h1 className="demo-view__title">{matter.name}</h1>
            <p className="demo-view__subtitle">
              {matter.client} · {matter.counterparty} · {scopeId === 'team' ? matter.team : 'Personal'}
            </p>
          </div>
        </div>

        <div className="demo-view__actions">
          <TonePill value={matter.status}>{matter.status}</TonePill>
          <span className="demo-meta-chip">{matter.nextDateLabel}: {matter.nextDate}</span>
          <button type="button" className="demo-button demo-button--ghost" onClick={() => onOpenSection('jolly')}>
            <Mic size={15} />
            Capture with Jolly
          </button>
          <button type="button" className="demo-button demo-button--primary">
            <Download size={15} />
            Export
          </button>
        </div>
      </div>

      <div className="demo-workspace-grid">
        <MatterResumePanel matter={matter} />

        <div className="demo-workspace-main">
          <div className="demo-section-tabs" role="tablist" aria-label="Matter sections">
            {DEMO_WORKSPACE_SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`demo-section-tab ${section.id === sectionId ? 'is-active' : ''}`}
                onClick={() => onOpenSection(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>

          <DemoPanel
            label="Active work"
            title={DEMO_WORKSPACE_SECTIONS.find((section) => section.id === sectionId)?.label || 'Overview'}
            className="demo-panel--scroll demo-panel--fill"
          >
            {renderMatterSection(sectionId, {
              matter,
              selectedFileId: uiState.selectedFileIdByMatter[matter.id],
              onSelectFile: (selectedFileId) => setUiState((current) => ({
                ...current,
                selectedFileIdByMatter: {
                  ...current.selectedFileIdByMatter,
                  [matter.id]: selectedFileId,
                },
              })),
              selectedCaptureId: uiState.selectedCaptureIdByMatter[matter.id],
              onSelectCapture: (selectedCaptureId) => setUiState((current) => ({
                ...current,
                selectedCaptureIdByMatter: {
                  ...current.selectedCaptureIdByMatter,
                  [matter.id]: selectedCaptureId,
                },
              })),
              selectedReviewId: uiState.selectedReviewIdByMatter[matter.id],
              onSelectReview: (selectedReviewId) => setUiState((current) => ({
                ...current,
                selectedReviewIdByMatter: {
                  ...current.selectedReviewIdByMatter,
                  [matter.id]: selectedReviewId,
                },
              })),
            })}
          </DemoPanel>
        </div>

        <OutputsPanel matter={matter} />
      </div>
    </div>
  );
}

export default function DemoPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [uiState, setUiState] = useState(buildInitialUiState);
  const [unlocked, setUnlocked] = useState(readDemoUnlockState);
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');

  const canonicalPath = normalizeDemoPath(location.pathname);
  const routeState = getDemoRouteState(canonicalPath);

  useEffect(() => {
    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, location.pathname, navigate]);

  useEffect(() => {
    const pageTitle = routeState.view === 'matter'
      ? `${routeState.matter.name} — Advoq Demo`
      : 'Matters — Advoq Demo';

    document.title = pageTitle;
    window.scrollTo({ top: 0, behavior: 'auto' });

    return () => {
      document.title = 'Advoq';
    };
  }, [routeState.view, routeState.matter?.name]);

  const handleScopeChange = (selectedScopeId) => {
    setUiState((current) => ({
      ...current,
      selectedScopeId,
    }));

    if (routeState.view === 'matter' && !isMatterVisibleInScope(routeState.matter.id, selectedScopeId)) {
      navigate(DEFAULT_DEMO_ROUTE, { replace: true });
    }
  };

  const resetDemo = () => {
    setUiState(buildInitialUiState());
    navigate(DEFAULT_DEMO_ROUTE, { replace: true });
  };

  const relockDemo = () => {
    writeDemoUnlockState(false);
    setUnlocked(false);
    setAccessCode('');
    setAccessError('');
    resetDemo();
  };

  const handleUnlock = (event) => {
    event.preventDefault();

    if (!isValidDemoAccessCode(accessCode)) {
      setAccessError('The access code did not match.');
      return;
    }

    writeDemoUnlockState(true);
    setUnlocked(true);
    setAccessCode('');
    setAccessError('');
  };

  if (!unlocked) {
    return (
      <AccessGate
        code={accessCode}
        error={accessError}
        onChange={(value) => {
          setAccessCode(value);
          if (accessError) setAccessError('');
        }}
        onSubmit={handleUnlock}
      />
    );
  }

  return (
    <div className="demo-shell">
      <header className="demo-topbar">
        <div className="demo-brand">
          <span className="demo-brand__mark">
            <BrandLogo variant="mark" className="demo-brand__mark-image" />
          </span>
          <div>
            <p className="demo-topbar__eyebrow">{CORPORATE_MA_DEMO.meta.title}</p>
            <strong className="demo-topbar__title">Demo</strong>
          </div>
        </div>

        <ScopeSwitcher selectedScopeId={uiState.selectedScopeId} onChange={handleScopeChange} />

        <div className="demo-topbar__actions">
          <button type="button" className="demo-button demo-button--ghost" onClick={resetDemo}>
            <RefreshCcw size={15} />
            Reset
          </button>
          <button type="button" className="demo-button demo-button--ghost" onClick={relockDemo}>
            <LockKeyhole size={15} />
            Relock
          </button>
        </div>
      </header>

      <div className="demo-stage">
        {routeState.view === 'matter' ? (
          <MatterView
            matter={routeState.matter}
            sectionId={routeState.section.id}
            scopeId={uiState.selectedScopeId}
            onBack={() => navigate(DEFAULT_DEMO_ROUTE)}
            onOpenSection={(sectionId) => navigate(buildDemoMatterPath(routeState.matter.id, sectionId))}
            uiState={uiState}
            setUiState={setUiState}
          />
        ) : (
          <HomeView
            scopeId={uiState.selectedScopeId}
            onOpenMatter={(matterId) => navigate(buildDemoMatterPath(matterId))}
          />
        )}
      </div>
    </div>
  );
}
